// 海外結帳(香港 / 中國)的純函式。
//
// 為什麼獨立成一支:台灣的結帳路徑(7-11 取貨、09 手機、綠界)已經跑了半年、
// 有一整套測試釘住,這次「完全不碰台灣路徑」——路由一開始就用 normalizeRegion()
// 分流,TW 照舊走原本的 schema 與 insert,香港/中國才進這裡的驗證與計價。
// 這裡不碰資料庫、不碰網路,所以可以直接拿真實輸入測,不需要 mock。

export const REGION = Object.freeze({ TW: "TW", HK: "HK", CN: "CN" });

export const REGION_LABEL = Object.freeze({
  TW: "台灣",
  HK: "香港",
  CN: "中國",
});

export const PAYMENT_METHOD = Object.freeze({
  ECPAY: "ecpay",
  FPS: "fps",
  WECHAT: "wechat",
});

export const PAYMENT_METHOD_LABEL = Object.freeze({
  ecpay: "綠界",
  fps: "轉數快 FPS",
  wechat: "微信支付",
});

// 綠界回呼 / 查單的 PaymentType 原值 → 後台看得懂的中文(前綴比對;官方一覽表
// https://developers.ecpay.com.tw/5686/)。微信支付實測回 WeiXin_OPAY(preflight\wechat 第 2 節 F)。
const ECPAY_PAYMENT_TYPE_PREFIX_LABEL = Object.freeze([
  ["WeiXin", "微信支付"],
  ["Credit_", "信用卡"],
  ["WebATM_", "網路 ATM"],
  ["ATM_", "ATM 轉帳"],
  ["CVS_", "超商代碼"],
  ["BARCODE_", "超商條碼"],
  ["ApplePay", "Apple Pay"],
  ["TWQR_", "台灣 Pay"],
  ["FPS", "轉數快 FPS"],
]);

/** 綠界 PaymentType(例 WeiXin_OPAY)→ 中文;空白回 "",認不得就原樣顯示(總比藏起來好)。 */
export function ecpayPaymentTypeLabel(paymentType) {
  // 空白:沒有任何前綴吻合 → 原樣回傳 "",不用另外特判
  const raw = String(paymentType ?? "").trim();
  const hit = ECPAY_PAYMENT_TYPE_PREFIX_LABEL.find(([prefix]) =>
    raw.startsWith(prefix),
  );
  return hit ? hit[1] : raw;
}

/**
 * 後台「付款方式」欄:結帳時選的(payment_method)+ 綠界實際回報的(payment_type)。
 * 台灣客人走綠界「ALL」也可能在綠界頁選微信支付 → 顯示「綠界・微信支付」,不會只看到「綠界」。
 */
export function orderPaymentLabel(order) {
  const method = order?.payment_method;
  // FPS / 微信支付本身就是付款工具,不用再補;其他(綠界、舊單沒有這欄)才補綠界實際工具
  if (method === PAYMENT_METHOD.FPS || method === PAYMENT_METHOD.WECHAT)
    return PAYMENT_METHOD_LABEL[method];
  const base = PAYMENT_METHOD_LABEL.ecpay;
  const detail = ecpayPaymentTypeLabel(order?.payment_type);
  return detail ? `${base}・${detail}` : base;
}

export const DEFAULT_COUNTRY_CODE = Object.freeze({ HK: "+852", CN: "+86" });

// 訂單狀態:香港 FPS 建單後等客人轉帳;貼紙付款後店主可切成「製作中」
export const STATUS_AWAITING_TRANSFER = "awaiting_transfer";
export const STATUS_PRODUCING = "producing";

// FPS 訂單建單時就把 ecpay_trade_no 填成這個前綴,
// 讓 recoveryWorker(只撈 trade_no 為空的單)不會拿它去問綠界,
// reconcilerWorker 也認這個前綴跳過(綠界那邊本來就沒有這筆)。
export const FPS_TRADE_NO_PREFIX = "FPS_";

export const DEFAULT_LEAD_TIME_NOTICE = "製作到寄送需 14–21 天";

// 2026-10-16 店主要求香港 / 中國多一個「私訊 Instagram」管道:
// 預設文字從「官方 LINE」改成「官方 LINE 或 Instagram」。舊預設留著給 migration 比對
// (只有現值仍等於舊預設才換,店主自己改過的不動),見 20261016_overseas_ig.sql。
export const LEGACY_OVERSEAS_TEXT_DEFAULTS = Object.freeze({
  overseas_wechat_pending_notice: "微信支付開通中,請先私訊官方 LINE",
  overseas_fps_instructions:
    "轉帳後請截圖傳至官方 LINE,並附上訂單號,我們確認收款後就會開始處理。",
});

export const OVERSEAS_SETTING_DEFAULTS = Object.freeze({
  overseas_fps_id: "",
  overseas_fps_name: "",
  overseas_hkd_rate: "4.1",
  overseas_shipping_hk: "250",
  overseas_shipping_cn: "300",
  overseas_wechat_enabled: "關",
  overseas_lead_time_notice: DEFAULT_LEAD_TIME_NOTICE,
  overseas_cart_lead_time_notice: "",
  overseas_wechat_pending_notice:
    "微信支付開通中,請先私訊官方 LINE 或 Instagram",
  overseas_fps_instructions:
    "轉帳後請截圖傳至官方 LINE 或 Instagram,並附上訂單號,我們確認收款後就會開始處理。",
  sticker_producing_email_body:
    "你的貼紙訂單已經開始製作囉!再次提醒:製作到寄送需 14–21 天,寄出時會再通知你。",
  // 綠界付款頁原文:「依微信支付規定僅開放以中國大陸身分證驗證的用戶支付此訂單」
  // (預跑 preflight\wechat\report.md 第 1 節);選中國時先講,減少付不了才來問
  overseas_wechat_realname_notice: "需中國大陸實名認證的微信才能付款",
  // 「加官方 LINE」按鈕連結;空白=沿用品牌設定的 LINE ID(brand_line_id),兩個都空就不顯示按鈕
  overseas_line_url: "",
  // 「私訊 Instagram」按鈕連結(instagram.com/帳號 或 @帳號);後台清空=不顯示按鈕
  overseas_ig_url: "https://www.instagram.com/catch___official/",
});

export const OVERSEAS_SETTING_KEYS = Object.freeze(
  Object.keys(OVERSEAS_SETTING_DEFAULTS),
);

const TRUTHY = new Set([
  "1",
  "true",
  "yes",
  "on",
  "開",
  "開啟",
  "是",
  "已開通",
]);

/** 後台用文字框存開關,店主可能填「開」「是」「true」…都要認得。 */
export function parseBooleanSetting(value) {
  // String(true) = "true"、String(null) = "null"(不在清單裡)→ 不用另外特判
  return TRUTHY.has(String(value).trim().toLowerCase());
}

function toPositiveNumber(value, fallback) {
  // Number() 自己會去掉前後空白;Number("") / Number(null) = 0 → 退回預設
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function toNonNegativeInt(value, fallback) {
  const raw = String(value ?? "").trim();
  if (raw === "") return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : fallback;
}

function textOr(value, fallback) {
  const s = value == null ? "" : String(value).trim();
  return s === "" ? fallback : s;
}

// LINE ID 允許的字元(官方帳號 @ 開頭;個人 ID 英數 . _ -)
const LINE_ID_RE = /^@?[A-Za-z0-9._-]{2,40}$/;

/**
 * 把後台填的 LINE 連結 / LINE ID 轉成可點的「加好友」網址。
 * - 填完整網址(https://lin.ee/… 或 https://line.me/…)→ 原樣用
 * - 官方帳號 ID(@381zyydb,後台常被打成全形「＠」)→ https://line.me/R/ti/p/%40381zyydb
 * - 個人 ID → https://line.me/ti/p/~id
 * - 空白或看不懂 → "",前台就不顯示按鈕(不放一顆點了會壞的鈕)
 */
export function lineAddFriendUrl(value) {
  // null / undefined 要先擋:String(null) = "null" 會被當成一個合法的個人 ID
  if (value == null) return "";
  // 空字串不用特判:LINE_ID_RE 至少要 2 個字,自然回 ""
  const raw = String(value).replace(/＠/g, "@").replace(/\s/g, "");
  if (/^https:\/\/(lin\.ee|line\.me|page\.line\.me)\//i.test(raw)) return raw;
  if (!LINE_ID_RE.test(raw)) return "";
  return raw.startsWith("@")
    ? `https://line.me/R/ti/p/%40${raw.slice(1)}`
    : `https://line.me/ti/p/~${raw}`;
}

// IG 帳號:1–30 字,英數 . _(官方規則)
const IG_HANDLE = "([A-Za-z0-9._]{1,30})";
const IG_PROFILE_RE = new RegExp(
  `^(?:https?://)?(?:www\\.|m\\.)?instagram\\.com/${IG_HANDLE}/?(?:[?#].*)?$`,
  "i",
);
const IG_HANDLE_RE = new RegExp(`^@?${IG_HANDLE}$`);

/**
 * 把後台填的 Instagram 網址 / 帳號轉成「直接開私訊」的網址。
 * - instagram.com/帳號(有無 https、www、結尾 / 或 ?參數都認)→ https://ig.me/m/帳號
 * - @帳號 或 帳號(後台常被打成全形「＠」)→ https://ig.me/m/帳號
 * - 其他 https 網址(例如已經是 ig.me 連結)→ 原樣用(http:// 與 javascript: 之類不收)
 * - 空白或看不懂 → "",前台就不顯示按鈕(不放一顆點了會壞的鈕)
 */
export function instagramDmUrl(value) {
  // null / undefined 要先擋:String(null) = "null" 會被當成一個合法的帳號
  if (value == null) return "";
  // 空字串不用特判:IG_HANDLE_RE 至少要 1 個字,自然回 ""
  const raw = String(value).replace(/＠/g, "@").replace(/\s/g, "");
  const handle = (raw.match(IG_PROFILE_RE) || raw.match(IG_HANDLE_RE))?.[1];
  if (handle) return `https://ig.me/m/${handle}`;
  return /^https:\/\//i.test(raw) ? raw : "";
}

/**
 * 把 site_settings 字典(key → 字串)轉成結帳要用的設定。
 * 數字欄位填錯(空白/負數/非數字)一律退回預設,不讓一個錯字把運費變成 0。
 */
export function buildOverseasConfig(settings = {}) {
  const s = settings || {};
  const d = OVERSEAS_SETTING_DEFAULTS;
  return {
    fpsId: textOr(s.overseas_fps_id, d.overseas_fps_id),
    fpsName: textOr(s.overseas_fps_name, d.overseas_fps_name),
    hkdRate: toPositiveNumber(s.overseas_hkd_rate, Number(d.overseas_hkd_rate)),
    shippingHK: toNonNegativeInt(
      s.overseas_shipping_hk,
      Number(d.overseas_shipping_hk),
    ),
    shippingCN: toNonNegativeInt(
      s.overseas_shipping_cn,
      Number(d.overseas_shipping_cn),
    ),
    wechatEnabled: parseBooleanSetting(
      s.overseas_wechat_enabled ?? d.overseas_wechat_enabled,
    ),
    leadTimeNotice: textOr(
      s.overseas_lead_time_notice,
      d.overseas_lead_time_notice,
    ),
    // 購物車(數位圖檔)不用寄送,預設不顯示;店主要顯示就自己填
    cartLeadTimeNotice: textOr(s.overseas_cart_lead_time_notice, ""),
    wechatPendingNotice: textOr(
      s.overseas_wechat_pending_notice,
      d.overseas_wechat_pending_notice,
    ),
    wechatRealnameNotice: textOr(
      s.overseas_wechat_realname_notice,
      d.overseas_wechat_realname_notice,
    ),
    fpsInstructions: textOr(
      s.overseas_fps_instructions,
      d.overseas_fps_instructions,
    ),
    producingEmailBody: textOr(
      s.sticker_producing_email_body,
      d.sticker_producing_email_body,
    ),
    // 海外專用連結優先,沒填就用品牌設定的 LINE ID(呼叫端把 brand 設定一起傳進來)
    lineUrl:
      lineAddFriendUrl(s.overseas_line_url) ||
      lineAddFriendUrl(s.brand_line_id),
    // 沒有這列(migration 未套)才用預設帳號;店主清空 = 不顯示,所以不能用 textOr
    igUrl: instagramDmUrl(s.overseas_ig_url ?? d.overseas_ig_url),
  };
}

/** 不認得的地區一律當台灣 —— 舊前端沒送 region 時行為完全不變。 */
export function normalizeRegion(value) {
  const v = String(value ?? "")
    .trim()
    .toUpperCase();
  return v === REGION.HK || v === REGION.CN ? v : REGION.TW;
}

export function paymentMethodForRegion(region) {
  const r = normalizeRegion(region);
  if (r === REGION.HK) return PAYMENT_METHOD.FPS;
  if (r === REGION.CN) return PAYMENT_METHOD.WECHAT;
  return PAYMENT_METHOD.ECPAY;
}

/** "852" / "+852" / " +852 " → "+852";格式不對回 null。 */
export function normalizeCountryCode(value) {
  // null / undefined 會變成 "+null" 之類,下面的格式檢查自然擋掉
  const raw = String(value).trim();
  const withPlus = raw.startsWith("+") ? raw : `+${raw}`;
  return /^\+\d{1,4}$/.test(withPlus) ? withPlus : null;
}

/**
 * 國碼電話驗證。回 { ok, phone, error }。
 * phone 存成 "+852 91234567"(國碼 + 空白 + 號碼),直接寫進 buyer_phone,
 * 後台與寄件單一眼就看得出是哪國的號碼。
 */
export function validateOverseasPhone(countryCode, number) {
  const cc = normalizeCountryCode(countryCode);
  if (!cc) return { ok: false, error: "國碼格式錯誤,例如 +852" };
  const digits = String(number).replace(/[\s-]/g, "");
  if (!/^\d+$/.test(digits)) {
    return { ok: false, error: "電話只能填數字" };
  }
  if (cc === "+852" && digits.length !== 8) {
    return { ok: false, error: "香港電話應為 8 碼" };
  }
  if (cc === "+86" && !/^1\d{10}$/.test(digits)) {
    return { ok: false, error: "中國手機應為 1 開頭的 11 碼" };
  }
  // E.164:國碼 + 號碼最多 15 碼(國碼至少 1 碼,所以號碼自然 ≤ 14)
  if (digits.length < 5 || cc.length - 1 + digits.length > 15) {
    return { ok: false, error: "電話長度不正確" };
  }
  return { ok: true, phone: `${cc} ${digits}` };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 香港 / 中國訂單的欄位驗證(伺服器端;前端驗證只是體驗,這裡才算數)。
 * product: "sticker"(實體寄送,要地址)或 "artwork"(線上圖檔,不用地址)
 * 回 { ok, errors[], data }。
 */
export function validateOverseasOrder({
  region,
  product,
  buyerName,
  buyerEmail,
  phoneCountryCode,
  buyerPhone,
  shippingAddress,
} = {}) {
  const errors = [];
  const r = normalizeRegion(region);
  if (r === REGION.TW) {
    errors.push("海外訂單地區必須是香港或中國");
  }
  const name = String(buyerName ?? "").trim();
  if (!name) errors.push("請填寫姓名");
  else if (name.length > 50) errors.push("姓名過長");

  const email = String(buyerEmail ?? "").trim();
  if (!email) errors.push("請填寫 Email");
  else if (!EMAIL_RE.test(email) || email.length > 254) {
    errors.push("Email 格式錯誤");
  }

  const phoneResult = validateOverseasPhone(
    phoneCountryCode ?? DEFAULT_COUNTRY_CODE[r],
    buyerPhone,
  );
  if (!phoneResult.ok) errors.push(phoneResult.error);

  const address = String(shippingAddress ?? "").trim();
  if (product === "sticker") {
    if (!address) errors.push("請填寫收件地址");
    else if (address.length < 5) errors.push("收件地址太短,請填完整地址");
    else if (address.length > 300) errors.push("收件地址過長");
  }

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    errors: [],
    data: {
      region: r,
      paymentMethod: paymentMethodForRegion(r),
      buyerName: name,
      buyerEmail: email,
      buyerPhone: phoneResult.phone,
      shippingAddress: product === "sticker" ? address : null,
    },
  };
}

/** 運費:台灣用原本的貼紙運費,港/中用後台設定。 */
export function computeShippingFee(region, { twShipping = 0, config } = {}) {
  const r = normalizeRegion(region);
  const cfg = config || buildOverseasConfig({});
  if (r === REGION.HK) return cfg.shippingHK;
  if (r === REGION.CN) return cfg.shippingCN;
  return twShipping;
}

/**
 * 台幣 → 港幣,進位到整數港幣(寧可客人多轉幾毛,不要少收)。
 * 先四捨五入到「分」再無條件進位,避免 41 / 4.1 = 10.000000000000002 被進位成 11。
 */
export function twdToHkd(twd, rate) {
  const amount = Number(twd);
  const r = Number(rate);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  if (!Number.isFinite(r) || r <= 0) return 0;
  return Math.ceil(Math.round((amount / r) * 100) / 100);
}

/** 感謝頁 / 確認信要顯示的 FPS 付款資訊(建單當下的快照)。 */
export function buildFpsPaymentInfo({
  orderNo,
  totalTwd,
  config,
  product = "sticker",
}) {
  const cfg = config || buildOverseasConfig({});
  return {
    orderNo,
    fpsId: cfg.fpsId,
    fpsName: cfg.fpsName,
    amountTwd: Number(totalTwd) || 0,
    amountHkd: twdToHkd(totalTwd, cfg.hkdRate),
    hkdRate: cfg.hkdRate,
    instructions: cfg.fpsInstructions,
    // 圖檔是線上交付,沒有「製作到寄送」;用購物車那條提醒(預設空白 = 不顯示)
    leadTimeNotice:
      product === "artwork" ? cfg.cartLeadTimeNotice : cfg.leadTimeNotice,
    // 「轉帳後截圖傳官方 LINE」旁邊的加好友按鈕;空字串 = 不顯示
    lineUrl: cfg.lineUrl || "",
    // 同一處的「私訊 Instagram」按鈕;空字串 = 不顯示
    igUrl: cfg.igUrl || "",
  };
}

/** 中國 + 微信支付尚未開通 → 選項看得到但不能送出。 */
export function isRegionCheckoutBlocked(region, config) {
  const cfg = config || buildOverseasConfig({});
  return normalizeRegion(region) === REGION.CN && !cfg.wechatEnabled;
}

/**
 * 判斷 Supabase 錯誤是不是「新欄位 / 新狀態還沒套 migration」。
 * 是的話呼叫端要降級(讀:退回舊欄位;寫:回 503 說功能即將開放),
 * 而不是把整個頁面或台灣結帳弄壞。
 */
export function isMissingSchemaError(error) {
  if (!error) return false;
  const code = String(error.code);
  if (code === "PGRST204" || code === "42703" || code === "23514") return true;
  const msg = String(error.message);
  return (
    /column .* does not exist/i.test(msg) ||
    /Could not find the .* column/i.test(msg) ||
    /violates check constraint/i.test(msg)
  );
}

/**
 * 先跑 primary(含新欄位的查詢),若錯誤是 schema 缺失就改跑 fallback(舊欄位)。
 * 兩個參數都是回傳 { data, error } 的 async 函式。
 */
export async function runWithColumnFallback(primary, fallback) {
  const first = await primary();
  if (isMissingSchemaError(first?.error)) {
    return fallback();
  }
  return first;
}
